/** Flat product mockups for the Phase 9 designer (11.2 rule 3). SVG only: 1px lines, square corners, no shadows. */
const INK = "#1a1a1a";
const PAPER = "#ffffff";
const LINE = "#767676";

/** "8.5 x 11 - Full Page" → [8.5, 11]; named sizes fall back to letter. */
export function parseSize(value, fallback = [8.5, 11]) {
    const m = String(value || "").match(/(\d+(?:\.\d+)?)\s*"?\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!m) return fallback;
    const w = Number(m[1]), h = Number(m[2]);
    return w > 0 && h > 0 ? [w, h] : fallback;
}

function Art({ id, url, box, fit, pan }) {
    if (!url) return null;
    const [x, y, w, h] = box;
    const dx = (pan?.x || 0) * w / 100, dy = (pan?.y || 0) * h / 100;
    return (
        <image href={url} x={x + dx} y={y + dy} width={w} height={h} clipPath={`url(#${id})`}
            preserveAspectRatio={fit === "contain" ? "xMidYMid meet" : "xMidYMid slice"} data-testid="mockup-art" />
    );
}

/**
 * One SVG per mockup kind. `box` is where the art goes; the clip keeps it on the product.
 * kinds: sheet · folded · hanger · pad · envelope · aframe · sticker · sticker-sheet
 */
export default function Mockup({ kind = "sheet", url, fit = "cover", pan, size, side = "front", folds = 3, window = false, className = "" }) {
    const id = `clip-${kind}-${side}`;
    const [sw, sh] = parseSize(size);
    const ratio = sw / sh;
    const W = 400, H = 300;
    const pad = 24;
    const fitBox = (maxW, maxH) => {
        let w = maxW, h = maxW / ratio;
        if (h > maxH) { h = maxH; w = maxH * ratio; }
        return [(W - w) / 2, (H - h) / 2, w, h];
    };
    let body = null;
    let clip = null;
    let box = fitBox(W - pad * 2, H - pad * 2);
    if (kind === "folded") {
        const n = Math.max(2, Math.min(folds, 4));
        box = fitBox(W - pad * 2, H - pad * 2);
        const [x, y, w, h] = box;
        clip = <rect x={x} y={y} width={w} height={h} />;
        body = (
            <>
                <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />
                {Array.from({ length: n - 1 }, (_, i) => <line key={i} x1={x + (w / n) * (i + 1)} y1={y} x2={x + (w / n) * (i + 1)} y2={y + h} stroke={LINE} strokeDasharray="4 4" />)}
            </>
        );
    } else if (kind === "hanger") {
        box = fitBox(140, H - pad * 2);
        const [x, y, w, h] = box;
        const r = w * 0.22, cx = x + w / 2, cy = y + r + 10;
        const path = `M${x},${y} H${x + w} V${y + h} H${x} Z`;
        clip = <path d={path} />;
        body = (
            <>
                <path d={path} fill={PAPER} stroke={INK} />
                <circle cx={cx} cy={cy} r={r} fill="var(--page-bg, #f5f5f5)" stroke={INK} />
                <line x1={cx + r * 0.3} y1={cy + r * 0.9} x2={x + w - 6} y2={y + h * 0.42} stroke={INK} />
            </>
        );
    } else if (kind === "pad") {
        box = fitBox(W - pad * 2 - 24, H - pad * 2 - 24);
        const [x, y, w, h] = box;
        clip = <rect x={x} y={y + 16} width={w} height={h - 16} />;
        body = (
            <>
                <rect x={x + 14} y={y + 14} width={w} height={h} fill={PAPER} stroke={LINE} />
                <rect x={x + 7} y={y + 7} width={w} height={h} fill={PAPER} stroke={LINE} />
                <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />
                <rect x={x} y={y} width={w} height={16} fill={INK} />
            </>
        );
    } else if (kind === "envelope") {
        box = [pad, 70, W - pad * 2, 160];
        const [x, y, w, h] = box;
        clip = <rect x={x} y={y} width={w} height={h} />;
        body = (
            <>
                <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />
                {side === "back"
                    ? <path d={`M${x},${y} L${x + w / 2},${y + h * 0.55} L${x + w},${y} Z`} fill="none" stroke={INK} />
                    : window && <rect x={x + w * 0.1} y={y + h * 0.5} width={w * 0.5} height={h * 0.28} fill="var(--page-bg, #f5f5f5)" stroke={LINE} />}
            </>
        );
    } else if (kind === "aframe") {
        const top = 36, bottom = H - 20, lx = 110, rx = 290;
        box = [lx + 10, top + 22, rx - lx - 20, 180];
        const [x, y, w, h] = box;
        clip = <rect x={x} y={y} width={w} height={h} />;
        body = (
            <>
                <path d={`M${lx + 20},${top} L${rx - 20},${top} L${rx + 30},${bottom} L${rx + 14},${bottom} L${rx - 28},${top + 24} L${lx + 28},${top + 24} L${lx - 14},${bottom} L${lx - 30},${bottom} Z`} fill={PAPER} stroke={INK} />
                <path d={`M${lx + 20},${top} L${rx - 20},${top} L${rx + 30},${bottom} L${lx - 30},${bottom} Z`} fill="none" stroke={INK} />
                <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />
            </>
        );
    } else if (kind === "sticker") {
        box = fitBox(220, 220);
        const [x, y, w, h] = box;
        clip = <rect x={x} y={y} width={w} height={h} />;
        body = (
            <>
                <rect x={x - 10} y={y - 10} width={w + 20} height={h + 20} fill={PAPER} stroke={LINE} strokeDasharray="3 3" />
                <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />
            </>
        );
    } else if (kind === "sticker-sheet") {
        box = fitBox(W - pad * 2, H - pad * 2);
        const [x, y, w, h] = box;
        const cols = 3, rows = 5, cw = w / cols, ch = h / rows;
        clip = <rect x={x} y={y} width={w} height={h} />;
        body = (
            <>
                <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />
                {Array.from({ length: cols * rows }, (_, i) => (
                    <rect key={i} x={x + (i % cols) * cw + 4} y={y + Math.floor(i / cols) * ch + 4} width={cw - 8} height={ch - 8} fill="none" stroke={LINE} strokeDasharray="3 3" />
                ))}
            </>
        );
    } else {
        const [x, y, w, h] = box;
        clip = <rect x={x} y={y} width={w} height={h} />;
        body = <rect x={x} y={y} width={w} height={h} fill={PAPER} stroke={INK} />;
    }
    const artBox = kind === "sticker-sheet" ? [box[0] + 4, box[1] + 4, box[2] / 3 - 8, box[3] / 5 - 8] : box;
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label={`${kind} preview`} data-testid={`mockup-${kind}`}>
            <defs><clipPath id={id}>{clip}</clipPath></defs>
            {body}
            {kind === "sticker-sheet" && url
                ? Array.from({ length: 15 }, (_, i) => (
                    <image key={i} href={url} x={box[0] + (i % 3) * (box[2] / 3) + 4} y={box[1] + Math.floor(i / 3) * (box[3] / 5) + 4}
                        width={artBox[2]} height={artBox[3]} preserveAspectRatio={fit === "contain" ? "xMidYMid meet" : "xMidYMid slice"} />
                ))
                : <Art id={id} url={url} box={artBox} fit={fit} pan={pan} />}
        </svg>
    );
}
